import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import Slider from 'react-slick';
import { CarouselNavPositionEnum } from '../../../../enums/carousel.enums';
import { getThumbnailProps } from '../../../../utils/youtubeUtils';
import Icon from '../../../@shared/icon';
import ModalDialog from '../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../@shared/overlayCarouselPopup';
import { TGalleryItem as TSharedGaleryItem } from '../../../@shared/overlayCarouselPopup/types/overlayCarouselPopup.types';
import CarouselTransparentNavSection from '../../../@shared/carouselTransparentNavSection';
import { TGalleryItem } from '../../types/productSeriesOverview.types';
import { OverviewCarouselModel } from '../../models/overviewCarousel.model';
import { TOverviewCarouselProps } from '../../types/overviewCarousel.types';
import HeroCard from '../heroCard';
import OveriewCarouselArrow from './overviewCarouselArrow';
import OverviewCarouselIndicators from './overviewCarouselIndicators';
import { formatComponentNumber, googleClickOpenVideoEvent, googleClickSlideEvent } from '../../../../utils/analytics';

const OverviewCarousel: FC<TOverviewCarouselProps> = (props) => {
  const model = new OverviewCarouselModel(props);
  const [currentSlide, _setCurrentSlide] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoPaused, setVideoPaused] = useState(false);
  const [localSlides, setLocalSlides] = useState<Array<TGalleryItem>>([]);
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    if (model.slides) {
      const newSlides = model.slides?.map((eachSlide) => {
        const youTubeThumnail =
          eachSlide.role === 'extvideo'
            ? {
                rendition_desktop_size1200: { url: getThumbnailProps(eachSlide.full || '')?.thumbnailImgSrc || '' },
              }
            : {};
        return {
          ...eachSlide,
          isVideo: eachSlide.role === 'extvideo' || eachSlide.role == 'video',
          ...youTubeThumnail,
        };
      });
      setLocalSlides(newSlides);
    }
  }, [model.slides]); // eslint-disable-line

  useEffect(() => {
    setVideoPlaying(false);
  }, [currentSlide]);

  const setCurrentSlide = (index): void => {
    if (localSlides.length > 1) {
      if (currentSlide !== index) {
        _setCurrentSlide(index);
      }
    }
  };

  const goToPreviousSlide = (): void => {
    const prevSlideNo = currentSlide === 0 ? localSlides.length - 1 : currentSlide - 1;
    sliderRef.current?.slickGoTo(prevSlideNo);
    setVideoPlaying(false);
    setVideoPaused(false);

    googleClickSlideEvent(`Slide ${formatComponentNumber(prevSlideNo + 1)}`, localSlides[prevSlideNo]?.title);
  };

  const goToNextSlide = (): void => {
    const nextSlideNo = currentSlide === localSlides.length - 1 ? 0 : currentSlide + 1;
    sliderRef.current?.slickGoTo(nextSlideNo);
    setVideoPlaying(false);
    setVideoPaused(false);

    googleClickSlideEvent(`Slide ${formatComponentNumber(nextSlideNo + 1)}`, localSlides[nextSlideNo]?.title);
  };

  const openOverlayModal = (): void => setShowOverlay(true);
  const closeOverlayModal = (): void => setShowOverlay(false);

  const playCallback = useCallback(
    (title: string | undefined) => {
      setVideoPlaying(true);
      setVideoPaused(false);
      googleClickOpenVideoEvent(title);
    },
    [setVideoPlaying, setVideoPaused]
  );

  const pauseCallback = useCallback(() => {
    setVideoPlaying(false);
    setVideoPaused(true);
  }, [setVideoPlaying, setVideoPaused]);

  const endCallback = useCallback(() => {
    setVideoPlaying(false);
    setVideoPaused(false);
  }, [setVideoPlaying, setVideoPaused]);

  const settings = {
    infinite: true,
    dots: false,
    arrows: false,
    variableWidth: false,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: false,
    prevArrow: false,
    afterChange: (index): void => setCurrentSlide(index),
  };

  const getOverlayProps = useCallback((): TSharedGaleryItem[] => {
    return localSlides.map((slideData) => {
      const title =
        typeof slideData?.title === 'string'
          ? {
              value: slideData?.title,
            }
          : slideData?.title;

      const description =
        typeof slideData?.description === 'string'
          ? {
              value: slideData?.description,
            }
          : slideData?.description;

      return {
        ...slideData,
        desktopAlt: slideData.alt,
        mobileAlt: slideData.alt,
        isVideo: slideData.role === 'extvideo' || slideData.role === 'video',
        videoUrl: slideData.full,
        title: title,
        description: description,
        desktopRendition: slideData.rendition_desktop_size1200,
        mobileRendition: slideData.rendition_mobile_size500,
      };
    });
  }, [localSlides]);

  return (
    <div className="series-overview-carousel">
      <div className="series-overview-carousel__wrapper">
        <Slider {...settings} ref={sliderRef}>
          {localSlides?.map((slide, index) => {
            return (
              <HeroCard
                slide={slide}
                playCallback={() => playCallback(slide?.title)}
                pauseCallback={pauseCallback}
                endCallback={endCallback}
                activeCard={index === currentSlide}
              />
            );
          })}
        </Slider>
        <div
          className={`series-overview-carousel__expand-icon-wrapper ${
            'series-overview-carousel__animate-icons series-overview-carousel__animate-icons' + (videoPlaying ? '--hide' : '--show')
          }`}
          onClick={openOverlayModal}
        >
          <Icon iconName="action-expand" extraClassName="series-overview-carousel__expand-icon" size={24} />
        </div>
        {localSlides?.length > 1 && (
          <>
            <OveriewCarouselArrow
              direction={'previous'}
              onClick={goToPreviousSlide}
              customClass={`
                series-overview-carousel__animate-icons
                ${videoPlaying && 'series-overview-carousel__animate-icons--animate-fade'}
                ${videoPaused && 'series-overview-carousel__animate-icons--show'}
              `}
            />

            <CarouselTransparentNavSection position={CarouselNavPositionEnum.LEFT} handleTouchStart={goToPreviousSlide} />
            <OverviewCarouselIndicators
              numOfSlides={localSlides?.length}
              currentSlideIndex={currentSlide}
              customClass={videoPlaying || videoPaused ? 'series-overview-carousel__animate-icons--hide' : ''}
            />
            <CarouselTransparentNavSection position={CarouselNavPositionEnum.RIGHT} handleTouchStart={goToNextSlide} />
            <OveriewCarouselArrow
              direction={'next'}
              onClick={goToNextSlide}
              customClass={`
                series-overview-carousel__animate-icons
                ${videoPlaying && 'series-overview-carousel__animate-icons--animate-fade'}
                ${videoPaused && 'series-overview-carousel__animate-icons--show'}
              `}
            />
          </>
        )}
        {localSlides?.length > 0 && (
          <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
            <OverlayCarouselPopup
              slides={getOverlayProps()}
              showNavDeck={true}
              showOverlayTitleAndDescription={true}
              analyticsMainTitle={props?.fields?.apiData?.title}
              useAnalytics
            />
          </ModalDialog>
        )}
      </div>
    </div>
  );
};

export default OverviewCarousel;
